import { useState, useEffect } from "react";
import { Filter, X, Maximize2, Minimize2, ChevronDown, ChevronUp } from "lucide-react";
import LinkedEntityFilters from "./LinkedEntityFilters";

/**
 * Sticky, always-visible filter bar with a Fullscreen toggle. Used by
 * Tardies / Incidents / DAR. Filters value shape: { client_names, site_ids, guard_ids }.
 *
 * Optional `collapsible` prop shows a chevron button that folds the filter
 * body away. The last-seen state persists to localStorage under
 * `filters_open_${testIdPrefix}` so users see the same layout on return.
 *
 * Fullscreen pattern (matches the Roster):
 *  - When NOT in fullscreen, clicking the button opens `fullscreenUrl` in a
 *    new browser window/tab via window.open.
 *  - When already IN fullscreen (page rendered at the /fullscreen URL), the
 *    button labels "Close" and calls window.close().
 */
export default function StickyFiltersBar({
  testIdPrefix,
  value,
  onChange,
  visibleCount,
  totalCount,
  isFullscreen,
  fullscreenUrl,
  collapsible = false,
}) {
  const storageKey = `filters_open_${testIdPrefix}`;
  const [openBody, setOpenBody] = useState(() => {
    if (!collapsible) return true;
    if (typeof window === "undefined") return true;
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    if (!collapsible) return;
    try { window.localStorage.setItem(storageKey, openBody ? "1" : "0"); } catch { /* quota */ }
  }, [collapsible, openBody, storageKey]);

  const hasFilters = !!(value.client_names.length || value.site_ids.length || value.guard_ids.length);
  const clear = () => onChange({ client_names: [], site_ids: [], guard_ids: [] });
  const onFsClick = () => {
    if (isFullscreen) {
      window.close();
    } else if (fullscreenUrl) {
      window.open(fullscreenUrl, "_blank", "noopener");
    }
  };
  return (
    <div className="sticky top-0 z-20 border border-[#27272A] bg-[#0E0E0E]/95 backdrop-blur-md" data-testid={`${testIdPrefix}-sticky-filters`}>
      <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-[#27272A]">
        <button
          type="button"
          onClick={() => collapsible && setOpenBody((v) => !v)}
          className={`inline-flex items-center gap-2 ${collapsible ? "cursor-pointer hover:text-white" : "cursor-default"}`}
          data-testid={collapsible ? `${testIdPrefix}-filters-toggle` : undefined}
          aria-expanded={openBody}
        >
          <Filter className="size-3.5 text-zinc-500" />
          <span className="label-mute">Filters</span>
          {collapsible && (openBody ? <ChevronUp className="size-3 text-zinc-500" /> : <ChevronDown className="size-3 text-zinc-500" />)}
          {hasFilters && (
            <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] ml-2" data-testid={`${testIdPrefix}-filter-count`}>
              · {visibleCount} of {totalCount}
            </span>
          )}
        </button>
        <div className="inline-flex items-center gap-2">
          {hasFilters && (
            <button
              type="button"
              data-testid={`${testIdPrefix}-clear-filters`}
              onClick={clear}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A] px-2 py-1"
            >
              <X className="size-3" /> Clear
            </button>
          )}
          {(fullscreenUrl || isFullscreen) && (
            <button
              type="button"
              data-testid={isFullscreen ? `${testIdPrefix}-exit-fullscreen` : `${testIdPrefix}-fullscreen-toggle`}
              onClick={onFsClick}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-[#27272A] hover:border-zinc-500 px-2 py-1"
              title={isFullscreen ? "Close this window" : "Open in a new window"}
            >
              {isFullscreen ? <Minimize2 className="size-3" /> : <Maximize2 className="size-3" />}
              {isFullscreen ? "Close" : "Fullscreen"}
            </button>
          )}
        </div>
      </div>
      {openBody && (
        <div className="px-5 py-3">
          <LinkedEntityFilters testIdPrefix={`${testIdPrefix}-filter`} value={value} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

/** True when the current pathname ends with `/fullscreen`. Pages use this
 * during render to skip the PageHeader and stretch to fill the viewport. */
export function useFullscreenRoute(pathname) {
  return typeof pathname === "string" && pathname.endsWith("/fullscreen");
}
